import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material'
import { StackItem } from 'ui/layout/StackItem'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import { Caption, H2, Text1 } from 'ui/shorthands/Texts'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'
import BillingBankModal from 'ui/organisms/BillingBankModal'

import type {
  BankTransfer,
  ContactEmails,
  MedicalInstitutionProps,
} from 'types/med'
import { PharmacyProps } from 'types/common'
import StripeLink from 'ui/partials/StripeLink'
import LinkBuilder from 'ui/organisms/LinkBuilder'
import { createLInkJsFilePath } from 'app/medical/utils'
import AddEmailModalContent from './AddEmailModalContent'
import DeleteButton from 'ui/partials/DeleteButton'
import PosterPrintModal from './PosterPrintModal'
import BankInfo from 'ui/partials/BankInfo'
import PharmacySearch, {
  PharmacySearchProps,
} from 'ui/organisms/PharmacySearch'
import PharmacyInfo from 'ui/partials/PharmacyInfo'
import Stack from 'ui/layout/Stack'
import PrescriptionTypeModalContent, {
  PrescriptionTypeModalContentProps,
  prescriptionTypeOptions,
} from './PrescriptionTypeModalContent'
import EligibilityVerificationUrlModalContent from 'ui/partials/EligibilityVerificationUrlModalContent'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(1.5),
  },
  category: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '&:first-child': {
      marginTop: 0,
    },
  },
  stackContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stackContentTop: {
    alignItems: 'flex-start',
  },
  emailItem: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  innerContent: {
    paddingTop: theme.spacing(2),
  },
  pharmacy: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  pharmacies: {
    marginTop: theme.spacing(-2),
    marginRight: theme.spacing(-2),
  },
  addPharmacy: { padding: theme.spacing(2, 0) },
}))

export type GeneralSettingProps = {
  // 医療機関設定
  med: MedicalInstitutionProps
  // stripe設定
  stripe?: {
    connected: boolean
    onConnect: () => void // TODO: Promiseを返すようにする
    onDelete: () => Promise<void>
  }
  bank: {
    setting?: BankTransfer
    onSubmit: (data: BankTransfer) => void
  }
  email: {
    list: ContactEmails
    onUpdate: (emails: ContactEmails) => Promise<void | { message: string }>
  }
  prescription: {
    setting: PrescriptionTypeModalContentProps['setting']
    onSubmit: PrescriptionTypeModalContentProps['onSubmit']
  }
  pharmacy: {
    pharmacies?: PharmacyProps[]
    apiKey?: PharmacySearchProps['apiKey']
    onSearch: PharmacySearchProps['onSearch']
    onUpdate: (
      array: { scuel_id: PharmacyProps['scuel_id'] }[]
    ) => Promise<void>
  }
  eligibilityVerification: {
    url?: string
    onUpdate: (url: string) => Promise<void>
  }
}

const General: React.FC<GeneralSettingProps> = ({
  med,
  stripe,
  bank,
  email,
  pharmacy,
  prescription,
  eligibilityVerification,
}: GeneralSettingProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const [showBankSetting, setShowBankSetting] = useState<boolean>(false)
  const [showPharmacySearchModal, setSPharmacySearchModal] =
    useState<boolean>(false)
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false)
  const [showLinkBuilder, setShowLinkBuilder] = useState<boolean>(false)

  const [showPrescriptionTypeModal, setShowPrescriptionTypeModal] =
    useState<boolean>(false)
  const [showPosterModal, setShowPosterModal] = useState<boolean>(false)
  const [removeEmailIndex, setRemoveEmailIndex] = useState<number | undefined>()
  const [removingEmail, setRemovingEmail] = useState<boolean>(false)
  const [showDeleteStripeModal, setShowDeleteStripeModal] =
    useState<boolean>(false)
  const [
    showEligibilityVerificationUrlModal,
    setShowEligibilityVerificationUrlModal,
  ] = useState<boolean>(false)

  const [errorMessage, setErrorMessage] = useState<string>()

  const updatePharmacy: (param: {
    scuel_id: PharmacyProps['scuel_id']
    remove?: boolean
  }) => Promise<void> | undefined = ({ scuel_id, remove }) => {
    const array = pharmacy.pharmacies
      ? pharmacy.pharmacies.map(({ scuel_id }) => ({
          scuel_id,
        }))
      : []
    const index = array.findIndex((p) => p.scuel_id === scuel_id)
    if (remove) {
      array.splice(index, 1)
    } else if (index === -1) {
      array.push({ scuel_id })
    } else {
      return
    }
    return pharmacy.onUpdate(array)
  }

  useEffect(() => {
    // NOTE: リストが更新されるまでemail削除をdisabledのままにするため
    setRemovingEmail(false)
  }, [email.list])

  const deleteStripeModal = stripe && (
    <Modal
      handleClose={() => setShowDeleteStripeModal(false)}
      open={showDeleteStripeModal}
    >
      <ConfirmContent
        onCancel={() => setShowDeleteStripeModal(false)}
        onSubmit={() => {
          stripe.onDelete().finally(() => setShowDeleteStripeModal(false))
        }}
        title={t(
          'Stripeとの接続を解除すると、それまでの予約のStripe請求が行えなくなり、請求方法の変更が必要になります。接続を解除してよろしいですか？'
        )}
        submitLabel={t('接続解除')}
      />
    </Modal>
  )

  const bankSettingModal = bank && (
    <Modal
      large
      handleClose={() => {
        setShowBankSetting(false)
      }}
      open={showBankSetting}
    >
      <BillingBankModal
        bankTransfer={bank?.setting}
        onSubmit={(data) => {
          bank.onSubmit(data)
          setShowBankSetting(false)
        }}
        onClose={() => setShowBankSetting(false)}
      />
    </Modal>
  )

  const linkBuilderModal = (
    <Modal
      large
      handleClose={() => setShowLinkBuilder(false)}
      open={showLinkBuilder}
      footer={
        <Stack align={'right'}>
          <Button color={'primary'} onClick={() => setShowLinkBuilder(false)}>
            {t('閉じる')}
          </Button>
        </Stack>
      }
    >
      <LinkBuilder med={med} filePath={createLInkJsFilePath} />
    </Modal>
  )

  const emailModal = (
    <Modal
      large
      handleClose={() => setShowEmailModal(false)}
      open={showEmailModal}
    >
      <AddEmailModalContent
        emails={email.list}
        onSubmit={(emails) =>
          email.onUpdate(emails).catch(() => {
            setErrorMessage(t('errors.メールアドレスの追加に失敗しました'))
            setShowEmailModal(false)
          })
        }
        onClose={() => setShowEmailModal(false)}
      />
    </Modal>
  )

  const removeEmailModal = (
    <Modal
      handleClose={() => setRemoveEmailIndex(undefined)}
      open={typeof removeEmailIndex !== 'undefined'}
    >
      <ConfirmContent
        onCancel={() => setRemoveEmailIndex(undefined)}
        onSubmit={() => {
          if (typeof removeEmailIndex !== 'number') return
          setRemovingEmail(true)
          const newList = [...email.list]
          newList.splice(removeEmailIndex, 1)
          email
            .onUpdate(newList)
            .then(() => setRemoveEmailIndex(undefined))
            .catch(() => {
              setRemoveEmailIndex(undefined)
              setErrorMessage(t('errors.メールアドレスの削除に失敗しました'))
            })
        }}
        title={t('このメールアドレスを削除しますか？')}
        submitLabel={t('削除する')}
      />
    </Modal>
  )

  const handleClosePrescriptionTypeModal = () => {
    setShowPrescriptionTypeModal(false)
  }

  const prescriptionTypeModal = (
    <Modal
      large
      handleClose={handleClosePrescriptionTypeModal}
      open={showPrescriptionTypeModal}
    >
      <PrescriptionTypeModalContent
        setting={prescription.setting}
        onSubmit={(data) =>
          prescription
            .onSubmit(data)
            .then(() => {
              if (
                prescription.setting.prescription_medicine !== 'pharmacy' &&
                pharmacy?.pharmacies?.length
              ) {
                return pharmacy.onUpdate([])
              }
            })
            .catch(() =>
              setErrorMessage(
                t('errors.処方・薬剤設定の変更でエラーが発生しました')
              )
            )
        }
        onClose={handleClosePrescriptionTypeModal}
      />
    </Modal>
  )

  const eligibilityVerificationModal = (
    <Modal
      large
      open={showEligibilityVerificationUrlModal}
      handleClose={() => setShowEligibilityVerificationUrlModal(false)}
    >
      <EligibilityVerificationUrlModalContent
        url={eligibilityVerification?.url}
        onSubmit={(data) => {
          return eligibilityVerification.onUpdate(data.url)
        }}
        onClose={() => setShowEligibilityVerificationUrlModal(false)}
      />
    </Modal>
  )

  const caughtErrorModal = (
    <Modal handleClose={() => setErrorMessage(undefined)} open={!!errorMessage}>
      <ConfirmContent
        onSubmit={() => setErrorMessage(undefined)}
        title={errorMessage}
      />
    </Modal>
  )

  return (
    <div className={classes.root}>
      <H2>{t('予約フォーム')}</H2>
      <div className={classes.category}>
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('URL')}>
              {t('予約をするページのURLです。')}
            </QuestionTooltipWithLabel>
          }
        >
          <Link href={med.form_url} target="_blank" rel="noopener noreferrer">
            {med.form_url}
          </Link>
        </StackItem>
        <StackItem label={t('ボタン')} alignTop={'22px'}>
          <Box display="flex" alignItems="center">
            <Box flexGrow={1}>
              <Text1>
                {t(
                  '予約フォームへのボタンをウェブサイトに設置するためのコードを作成できます。'
                )}
              </Text1>
            </Box>
            <Box flexShrink={0}>
              <Button
                variant={'contained'}
                color={'primary'}
                onClick={() => setShowLinkBuilder(true)}
              >
                {t('コードを作成')}
              </Button>
              {linkBuilderModal}
            </Box>
          </Box>
        </StackItem>
        <StackItem label={t('ポスター')} alignTop={'22px'}>
          <Box display="flex" alignItems="center">
            <Box flexGrow={1}>
              <Text1>
                {t(
                  '予約フォームへアクセスできるQRコードを掲載したポスターを印刷できます。'
                )}
              </Text1>
            </Box>
            <Box flexShrink={0}>
              <Button
                variant={'contained'}
                color={'primary'}
                onClick={() => setShowPosterModal(true)}
              >
                {t('ポスターを印刷')}
              </Button>
              <PosterPrintModal
                med={med}
                open={showPosterModal}
                onClose={() => setShowPosterModal(false)}
              />
            </Box>
          </Box>
          <Caption>
            <Trans
              i18nKey={
                'trans.ご案内にお使いいただける資料は<Link>こちら</Link>からダウンロードできます。'
              }
              components={{
                Link: (
                  <Link
                    href={'https://www.yadoc.jp/quick/support/download'}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </Caption>
        </StackItem>
      </div>
      <H2>{t('オンライン資格確認')}</H2>
      <div className={classes.category}>
        <StackItem
          alignTop
          label={
            <QuestionTooltipWithLabel label={t('医療機関別URL')}>
              {t('オンライン資格確認の利用開始を設定します。')}
            </QuestionTooltipWithLabel>
          }
        >
          <Box>
            <div className={classes.stackContent}>
              <Text1>
                {eligibilityVerification.url ? t('登録済') : t('未登録')}
              </Text1>
              <Button
                variant={'contained'}
                color={'primary'}
                onClick={() => setShowEligibilityVerificationUrlModal(true)}
              >
                {eligibilityVerification.url ? t('変更') : t('設定')}
              </Button>
            </div>
            <Box>
              <Caption>{eligibilityVerification.url || t('URLなし')}</Caption>
            </Box>
          </Box>
          {eligibilityVerificationModal}
        </StackItem>
      </div>
      <QuestionTooltipWithLabel label={<H2>{t('決済方法')}</H2>}>
        {t('申込者にメールでお知らせします。')}
      </QuestionTooltipWithLabel>
      <div className={classes.category}>
        {stripe && (
          <StackItem label={t('クレジットカード')} alignTop={'22px'}>
            <div className={classes.stackContent}>
              <Text1>
                <QuestionTooltipWithLabel label={t('Stripe')}>
                  <Trans
                    i18nKey={
                      'trans.利用開始には、Stripeとの連携が必要です。...'
                    }
                  />
                </QuestionTooltipWithLabel>
                : {stripe.connected ? t('設定済') : t('未設定')}
              </Text1>
              {stripe.connected ? (
                <Button
                  variant={'contained'}
                  color={'primary'}
                  onClick={() => setShowDeleteStripeModal(true)}
                >
                  {t('接続を解除')}
                </Button>
              ) : (
                <Button
                  variant={'contained'}
                  color={'primary'}
                  onClick={() => {
                    stripe.onConnect()
                    setShowDeleteStripeModal(false)
                  }}
                >
                  {t('利用開始')}
                </Button>
              )}
            </div>
            <Text1>
              <StripeLink />
            </Text1>
            {deleteStripeModal}
            <div className={classes.innerContent}>
              <Text1>
                <QuestionTooltipWithLabel label={t('リンク決済')}>
                  {t(
                    'STORES、SONYペイメント等をご利用の場合にお使いください。サービスサイト側で請求リンクを作成し、YaDoc Quickの請求フォーム内に貼り付けて送信します。'
                  )}
                </QuestionTooltipWithLabel>
              </Text1>
            </div>
          </StackItem>
        )}
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('銀行振込')}>
              <Trans
                i18nKey={
                  'trans.利用開始には、振込先の設定が必要です。 <br />YaDoc Quickの請求フォームで請求金額と明細を送信します。'
                }
              />
            </QuestionTooltipWithLabel>
          }
          alignTop={!!bank.setting}
        >
          <div
            className={cx(
              classes.stackContent,
              bank.setting && classes.stackContentTop
            )}
          >
            {bank.setting ? (
              <BankInfo bankTransfer={bank.setting} />
            ) : (
              <Text1>
                {t('振込先')}: {t('未設定')}
              </Text1>
            )}
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() => setShowBankSetting(true)}
            >
              {bank.setting?.account_name ? t('編集') : t('設定')}
            </Button>
          </div>
        </StackItem>
        {bankSettingModal}
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('代金引換')}>
              <Trans
                i18nKey={
                  'trans.利用開始には、各社配送サービスとのご契約が必要です。...'
                }
              />
            </QuestionTooltipWithLabel>
          }
        />
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('対面時窓口')}>
              {t('YaDoc Quickの請求フォームで請求金額と明細を送信します。')}
            </QuestionTooltipWithLabel>
          }
        />
      </div>
      <div className={classes.stackContent}>
        <H2>{t('通知')}</H2>
        <Button
          variant={'contained'}
          color={'primary'}
          onClick={() => setShowEmailModal(true)}
        >
          {t('追加')}
        </Button>
        {emailModal}
      </div>
      <div className={classes.category}>
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('メールアドレス')}>
              <Trans
                i18nKey={
                  'trans.以下のときにメール通知が届きます。<br /> ・新規予約、変更、取消 <br />・請求送信'
                }
              />
            </QuestionTooltipWithLabel>
          }
          alignTop={email.list.length ? '22px' : false}
          childNoPadding
        >
          {email.list?.length ? (
            email.list.map(({ email }, index) => (
              <div key={email} className={classes.emailItem}>
                {email}
                <DeleteButton
                  color={'primary'}
                  onClick={() => setRemoveEmailIndex(index)}
                  disabled={removingEmail}
                />
              </div>
            ))
          ) : (
            <Text1>{t('設定なし')}</Text1>
          )}
          {removeEmailModal}
        </StackItem>
      </div>
      <H2>{t('処方・薬剤')}</H2>
      <div className={classes.category}>
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('処方・薬剤')}>
              {t('処方せん及び薬剤の、患者さんへの受け渡し方法を設定します。')}
            </QuestionTooltipWithLabel>
          }
        >
          <div className={classes.stackContent}>
            <Text1>
              {
                prescriptionTypeOptions.find(
                  ({ value }) =>
                    value === prescription.setting.prescription_medicine
                )?.label
              }
              {prescription.setting.is_todokusuri &&
                t('または、とどくすりサービス')}
            </Text1>
            <Button
              color={'primary'}
              variant={'contained'}
              onClick={() => setShowPrescriptionTypeModal(true)}
            >
              {t('変更')}
            </Button>
          </div>
          {prescriptionTypeModal}
        </StackItem>
        {prescription.setting.prescription_medicine === 'pharmacy' &&
          pharmacy.apiKey && (
            <StackItem
              label={
                <QuestionTooltipWithLabel label={t('薬局の選択(任意)')}>
                  {t('最大10件まで指定できます。')}
                </QuestionTooltipWithLabel>
              }
              alignTop={pharmacy.pharmacies?.length ? '22px' : false}
            >
              {pharmacy.pharmacies?.length ? (
                <div className={classes.pharmacies}>
                  {pharmacy.pharmacies.map((p) => (
                    <div key={p.scuel_id} className={classes.pharmacy}>
                      <PharmacyInfo
                        type={'med'}
                        pharmacy={p}
                        inLine
                        button={
                          <DeleteButton
                            color={'primary'}
                            onClick={() =>
                              updatePharmacy({
                                scuel_id: p.scuel_id,
                                remove: true,
                              })?.catch(() =>
                                setErrorMessage(
                                  t('errors.薬局の削除でエラーが発生しました')
                                )
                              )
                            }
                          />
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : null}
              <div className={classes.addPharmacy}>
                <Button
                  color={'primary'}
                  variant={'contained'}
                  onClick={() => setSPharmacySearchModal(true)}
                  disabled={(pharmacy.pharmacies?.length || 0) >= 10}
                >
                  {t('追加')}
                </Button>
              </div>
              <Modal
                large
                handleClose={() => setSPharmacySearchModal(false)}
                open={showPharmacySearchModal}
              >
                <PharmacySearch
                  selected={pharmacy?.pharmacies?.map(
                    ({ scuel_id }) => scuel_id
                  )}
                  type={'med'}
                  institutionAddress={[
                    med.prefecture,
                    med.city,
                    med.town,
                    med.street,
                    med.building,
                  ].join('')}
                  apiKey={pharmacy.apiKey}
                  onSearch={pharmacy.onSearch}
                  onSelect={({ scuel_id }) => {
                    setSPharmacySearchModal(false)
                    updatePharmacy({ scuel_id })?.catch(() =>
                      setErrorMessage(
                        t('errors.薬局の追加でエラーが発生しました')
                      )
                    )
                  }}
                  onCancel={() => setSPharmacySearchModal(false)}
                />
              </Modal>
            </StackItem>
          )}
      </div>
      {caughtErrorModal}
    </div>
  )
}
export default General
